import { ccmPageActionAllowedStatues } from 'constants/patient_statuses';
import { CCMStatusGroups } from 'infrastructure/consts/ccm-status-groups';
import { RoleGroups } from 'infrastructure/consts/role-groups';
import { RpmStatusGroups } from 'infrastructure/consts/rpm-status-groups';
import { Roles, UserType } from 'infrastructure/enums';

import type { CcmStatuses, RpmStatuses } from 'infrastructure/enums';

const isPatient = (userType: UserType) => userType === UserType.Patient;
const isPractice = (userType: UserType) => userType === UserType.Practice;
const isAdmin = (userType: UserType) => userType === UserType.Admin;
const isAgencyDoctor = (userType: UserType) => userType === UserType.HomeHealth;

const isRoleDefault = (roleNames: Roles[]) => roleNames.includes(Roles.Default);

const isRoleNameAdministrator = (roleNames: Roles[]) =>
  roleNames.includes(Roles.AdminPanelAccess);

export const hasRNRole = (roleNames: Roles[]) => {
  const rnRoles = [
    Roles.Doctor,
    Roles.PhysicianAssistant,
    Roles.QHCP,
    Roles.RegisteredNurse,
    Roles.NursePractitioner,
  ];
  return roleNames.some((r) => rnRoles.includes(r));
};

const isRoleNameAdminTools = (roleNames: Roles[]) =>
  roleNames.includes(Roles.AdminTools);

const isRoleNameBillingTools = (roleNames: Roles[]) =>
  roleNames.includes(Roles.BillingTools);

const isOnboardingPatient = (
  onboardingStatuses: RpmStatuses[],
  status: RpmStatuses,
) => onboardingStatuses.includes(status);

const isBiller = (roleNames: Roles[]) =>
  roleNames?.every((roleName) => roleName === Roles.Biller);

const isPatientAcceptance = (roleNames: Roles[]) =>
  roleNames.includes(Roles.PatientAcceptance);

const isCcmActionAllowedStatus = (ccmStatus?: CcmStatuses) =>
  ccmStatus ? ccmPageActionAllowedStatues.includes(ccmStatus) : false;

const hasClinicalRole = (roleNames: Roles[]) =>
  roleNames.some((roleName) => RoleGroups.ClinicalRoles.includes(roleName));

const isReadOnlyRole = (roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }
  return roleNames.every((role) => RoleGroups.ReadOnlyRoles.includes(role));
};

const isTurkUserRole = (roleNames: Roles[]) => roleNames?.includes(Roles.Turk);

const isNutritionist = (roleNames: Roles[]) =>
  roleNames.includes(Roles.Nutritionist);

const hasAccessToCallDashboard = (roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }
  return roleNames.includes(Roles.CallDashboard);
};

const hasPatientProfileAccess = (userType: UserType, roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  if (isAdmin(userType)) {
    return true;
  }

  if (isPatient(userType)) {
    return true;
  }

  if (isAgencyDoctor(userType)) {
    const hhaAccessRoles = [
      Roles.Biller,
      Roles.Support,
      Roles.ClientSuccessOnboarding,
      Roles.SalesRepresentative,
      Roles.ClientSuccessScaling,
      Roles.PhysicianAssistant,
      Roles.Doctor,
      Roles.QHCP,
      Roles.NursePractitioner,
      Roles.RegisteredNurse,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  if (isPractice(userType)) {
    const practiceAccessRoles = [
      Roles.Biller,
      Roles.Support,
      Roles.Onboarding,
      Roles.Doctor,
      Roles.NursePractitioner,
      Roles.PhysicianAssistant,
      Roles.RegisteredNurse,
      Roles.QHCP,
      Roles.PharmD,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => practiceAccessRoles.includes(role));
  }

  return false;
};

const hasCcmAgentDashboardAccess = (userType: UserType, roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  if (isAdmin(userType)) {
    return true;
  }

  if (isAgencyDoctor(userType)) {
    const hhaAccessRoles = [
      Roles.Biller,
      Roles.Doctor,
      Roles.NursePractitioner,
      Roles.PhysicianAssistant,
      Roles.QHCP,
      Roles.Support,
      Roles.Onboarding,
      Roles.RegisteredNurse,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  return false;
};

const hasInteractiveTimeDashboardAccess = (
  userType: UserType,
  roleNames: Roles[],
) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  if (isAdmin(userType)) {
    return true;
  }

  if (isAgencyDoctor(userType)) {
    const hhaAccessRoles = [
      Roles.Biller,
      Roles.Doctor,
      Roles.NursePractitioner,
      Roles.PhysicianAssistant,
      Roles.QHCP,
      Roles.Support,
      Roles.Onboarding,
      Roles.RegisteredNurse,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  if (isPractice(userType)) {
    const hhaAccessRoles = [
      Roles.PhysicianAssistant,
      Roles.NursePractitioner,
      Roles.RegisteredNurse,
      Roles.QHCP,
      Roles.Biller,
      Roles.Doctor,
      Roles.Support,
      Roles.Onboarding,
      Roles.PharmD,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  return false;
};

const hasPatientDetailsAccess = (userType: UserType, roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  if (isAdmin(userType)) {
    return true;
  }

  if (isAgencyDoctor(userType)) {
    const hhaAccessRoles = [
      Roles.RegisteredNurse,
      Roles.AdminPanelAccess,
      Roles.ClientSuccessScaling,
      Roles.Doctor,
      Roles.AtRiskAlerting,
      Roles.PhysicianAssistant,
      Roles.SalesRepresentative,
      Roles.Support,
      Roles.Onboarding,
      Roles.ClientSuccessOnboarding,
      Roles.NursePractitioner,
      Roles.CriticalAlerting,
      Roles.QHCP,
      Roles.Biller,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  if (isPractice(userType)) {
    const hhaAccessRoles = [
      Roles.Support,
      Roles.PharmD,
      Roles.AdminPanelAccess,
      Roles.RegisteredNurse,
      Roles.PhysicianAssistant,
      Roles.Biller,
      Roles.NursePractitioner,
      Roles.QHCP,
      Roles.Onboarding,
      Roles.Doctor,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  return false;
};

const hasCcmDashboardAccess = (userType: UserType, roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  if (isAdmin(userType)) {
    return false; // temp hide ccm console (CCM Home Dashboard)
  }

  if (isAgencyDoctor(userType)) {
    const hhaAccessRoles = [
      Roles.AdminPanelAccess,
      Roles.Doctor,
      Roles.PhysicianAssistant,
      Roles.NursePractitioner,
      Roles.RegisteredNurse,
      Roles.QHCP,
      Roles.Biller,
      Roles.Support,
      Roles.Onboarding,
      Roles.SalesRepresentative,
      Roles.ClientSuccessOnboarding,
      Roles.ClientSuccessScaling,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  return false;
};

const hasReportsAccess = (userType: UserType, roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  if (isAdmin(userType)) {
    return true;
  }

  if (isAgencyDoctor(userType)) {
    const hhaAccessRoles = [
      Roles.Biller,
      Roles.Doctor,
      Roles.NursePractitioner,
      Roles.PhysicianAssistant,
      Roles.MedicalAssistant,
      Roles.QHCP,
      Roles.Support,
      Roles.Onboarding,
      Roles.RegisteredNurse,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  if (isPractice(userType)) {
    const practiceAccessRoles = [
      Roles.PhysicianAssistant,
      Roles.NursePractitioner,
      Roles.RegisteredNurse,
      Roles.QHCP,
      Roles.Biller,
      Roles.Doctor,
      Roles.Support,
      Roles.Onboarding,
      Roles.PharmD,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => practiceAccessRoles.includes(role));
  }

  return false;
};

const isSmsDashboardOrUnregisteredRole = (roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  return RoleGroups.SmsDashboardRoles.some((role) => roleNames.includes(role));
};

const isSmsDashboardUnregisteredRole = (roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }
  return roleNames.some((role) => role === Roles.SmsDashboardUnregistered);
};

const isRoleNameOnboarding = (roleNames: Roles[]) =>
  roleNames.includes(Roles.Onboarding);

const hasPatientProfileRpm = (rpmStatus: RpmStatuses) =>
  RpmStatusGroups.PatientProfile.includes(rpmStatus);

const hasPatientProfileCcm = (ccmStatus: CcmStatuses) =>
  CCMStatusGroups.PatientProfile.includes(ccmStatus);

export const hasCcmConsoleAccess = (userType: UserType, roleNames: Roles[]) => {
  if (!roleNames || !roleNames.length) {
    return false;
  }

  if (isAdmin(userType)) {
    return true;
  }

  if (isAgencyDoctor(userType)) {
    const hhaAccessRoles = [
      Roles.AdminPanelAccess,
      Roles.Doctor,
      Roles.PhysicianAssistant,
      Roles.NursePractitioner,
      Roles.RegisteredNurse,
      Roles.QHCP,
      Roles.Biller,
      Roles.Support,
      Roles.Onboarding,
      Roles.SalesRepresentative,
      Roles.ClientSuccessOnboarding,
      Roles.ClientSuccessScaling,
      Roles.MedicalAssistant,
    ];
    return roleNames.some((role) => hhaAccessRoles.includes(role));
  }

  if (isPractice(userType)) {
    const practiceAccessRoles = [
      Roles.AdminPanelAccess,
      Roles.Doctor,
      Roles.PhysicianAssistant,
      Roles.MedicalAssistant,
      Roles.NursePractitioner,
      Roles.RegisteredNurse,
      Roles.QHCP,
      Roles.Biller,
      Roles.Support,
      Roles.PatientAcceptance,
      Roles.Onboarding,
      Roles.PharmD,
      Roles.SmsDashboard,
      Roles.AdminTools,
    ];
    return roleNames.some((role) => practiceAccessRoles.includes(role));
  }

  return false;
};

export {
  isPatient,
  isPractice,
  isAdmin,
  isAgencyDoctor,
  isRoleDefault,
  isRoleNameAdministrator,
  isRoleNameAdminTools,
  isRoleNameBillingTools,
  isOnboardingPatient,
  isBiller,
  isPatientAcceptance,
  isCcmActionAllowedStatus,
  hasClinicalRole,
  isReadOnlyRole,
  isTurkUserRole,
  isNutritionist,
  isSmsDashboardOrUnregisteredRole,
  isSmsDashboardUnregisteredRole,
  isRoleNameOnboarding,
  hasAccessToCallDashboard,
  hasPatientProfileRpm,
  hasPatientProfileCcm,
  hasPatientProfileAccess,
  hasCcmAgentDashboardAccess,
  hasInteractiveTimeDashboardAccess,
  hasPatientDetailsAccess,
  hasCcmDashboardAccess,
  hasReportsAccess,
};
